/** @format */

import { Disclosure } from "@headlessui/react";
import * as Sentry from "@sentry/react";
import type React from "react";

import Crash from "../assets/crash_low.webp";
import { queryClient } from "../queryClient";

import { isChunkLoadError } from "./lazyWithRetry";

interface ErrorBoundaryProps {
  error?: Error;
  componentStack?: string;
  eventId?: string;
  resetError?: () => void;
  children?: React.ReactNode;
}

/** Extract the first component name from a React component stack string. */
export function parseFailedComponent(componentStack?: string): string | null {
  if (!componentStack) return null;
  const match = componentStack.match(/^\s*at (\w+)/);
  return match?.[1] ?? null;
}

/** Detect whether an error is a network/fetch error. */
export function isNetworkError(error: Error): boolean {
  if (error.name === "AxiosError") return true;
  if (error.message === "Network Error") return true;
  if (error.message === "Failed to fetch") return true;
  if ("status" in error || "response" in error) return true;
  return false;
}

interface NetworkErrorInfo {
  url?: string;
  status?: number;
  statusText?: string;
  method?: string;
}

/** Extract network error details from an Axios-style error. */
export function getNetworkErrorInfo(error: Error): NetworkErrorInfo {
  const info: NetworkErrorInfo = {};
  const axiosError = error as Error & {
    config?: { url?: string; method?: string };
    response?: { status?: number; statusText?: string };
  };
  if (axiosError.config?.url) info.url = axiosError.config.url;
  if (axiosError.config?.method) info.method = axiosError.config.method.toUpperCase();
  if (axiosError.response?.status) info.status = axiosError.response.status;
  if (axiosError.response?.statusText) info.statusText = axiosError.response.statusText;
  return info;
}

function getIsStaff(): boolean {
  try {
    return queryClient.getQueryData<User>(["user"])?.isStaff ?? false;
  } catch {
    return false;
  }
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  error,
  componentStack,
  resetError,
  eventId,
  children = null,
}) => {
  if (!error) {
    return children;
  }

  const isChunkError = isChunkLoadError(error);
  const isNetwork = isNetworkError(error);
  const failedComponent = parseFailedComponent(componentStack);
  const isStaff = getIsStaff();

  // User-friendly UI for chunk load errors (stale deployment)
  if (isChunkError) {
    return (
      <div className="absolute inset-0 flex justify-center align-middle">
        <div className="bg-slate-900 border border-slate-700 rounded-3xl items-center flex flex-col justify-center h-min my-auto p-6 max-w-md">
          <h1 className="text-center text-2xl font-bold mb-2 text-blue-400">
            Update Available
          </h1>
          <p className="text-center text-slate-300 mb-4">
            A new version of the app is available. Please refresh to continue.
          </p>
          <button
            type="button"
            className="bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-md px-6 py-2"
            onClick={() => window.location.reload()}
          >
            Refresh Now
          </button>
        </div>
      </div>
    );
  }

  // Network error UI
  if (isNetwork) {
    const netInfo = getNetworkErrorInfo(error);
    return (
      <div className="absolute inset-0 flex justify-center align-middle">
        <div className="bg-slate-900 border border-slate-700 rounded-3xl items-center flex flex-col justify-center h-min my-auto p-6 max-w-lg">
          {failedComponent && (
            <p className="text-sm text-slate-400 mb-1">
              Error in{" "}
              <span className="font-semibold text-slate-200">{failedComponent}</span>
            </p>
          )}
          <h1 className="text-center text-2xl font-bold mb-2 text-amber-400">
            Network Error
          </h1>
          <div className="bg-slate-800 rounded-lg p-3 mb-4 w-full text-sm font-mono">
            {netInfo.method && netInfo.url && (
              <p className="text-slate-300 truncate">
                {netInfo.method} {netInfo.url}
              </p>
            )}
            {netInfo.status && (
              <p className="text-red-400">
                Status: {netInfo.status}
                {netInfo.statusText ? ` ${netInfo.statusText}` : ""}
              </p>
            )}
            {!netInfo.status && !netInfo.url && (
              <p className="text-slate-300">{error.message}</p>
            )}
          </div>
          <p className="text-center text-slate-400 text-sm mb-4">
            Please check your internet connection and try again.
          </p>
          {isStaff && (
            <StaffDebugPanel
              error={error}
              componentStack={componentStack}
              eventId={eventId}
            />
          )}
          <div className="flex gap-3">
            <button
              type="button"
              className="text-amber-400 border border-amber-400 rounded-md px-4 py-1.5 hover:bg-amber-400/10"
              onClick={() => resetError?.() ?? window.location.reload()}
            >
              Retry
            </button>
            {eventId && (
              <button
                type="button"
                className="bg-red-600 hover:bg-red-500 rounded-md text-white px-4 py-1.5"
                onClick={() => Sentry.showReportDialog({ eventId })}
              >
                Report feedback
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Standard error UI for render/other errors
  return (
    <div className="absolute inset-0 flex justify-center align-middle">
      <div className="bg-slate-900 border border-slate-700 rounded-3xl items-center flex flex-col justify-center h-min my-auto p-6 max-w-lg">
        {failedComponent && (
          <p className="text-sm text-slate-400 mb-1">
            Error in{" "}
            <span className="font-semibold text-slate-200">{failedComponent}</span>
          </p>
        )}
        <h1 className="text-center text-2xl font-bold mb-1 text-red-400">
          Something went wrong
        </h1>
        {isStaff ? (
          <>
            <h2 className="text-center text-sm mb-3 text-red-400 mx-2 font-mono">
              {error.toString()}
            </h2>
            <StaffDebugPanel
              error={error}
              componentStack={componentStack}
              eventId={eventId}
            />
          </>
        ) : (
          <p className="text-center text-sm text-slate-400 mb-3 mx-2">
            An unexpected error occurred. You can try reloading or report the issue.
          </p>
        )}
        <Disclosure as="div" className="w-full">
          {({ open }) => (
            <>
              <div className="w-full flex justify-center gap-3 mb-5">
                <button
                  type="button"
                  className="text-red-400 border border-red-400/50 rounded-md px-3 py-1.5 hover:bg-red-400/10"
                  onClick={() => resetError?.() ?? window.location.reload()}
                >
                  Reload
                </button>
                <Disclosure.Button className="text-slate-400 border border-slate-600 rounded-md px-3 py-1.5 hover:bg-slate-800">
                  {open ? "Hide" : "Show"} stack trace
                </Disclosure.Button>
                {eventId && (
                  <button
                    type="button"
                    className="bg-red-600 hover:bg-red-500 rounded-md text-white px-3 py-1.5"
                    onClick={() => Sentry.showReportDialog({ eventId })}
                  >
                    Report feedback
                  </button>
                )}
              </div>
              <div
                className={`grid ${
                  open ? "grid-cols-2" : "grid-cols-1"
                } grid-flow-col auto-cols-min auto-rows-min grid-rows-1 align-middle justify-center w-full shrink`}
              >
                <img
                  src={Crash}
                  alt="error"
                  className="object-contain w-1/2 m-auto"
                  width="1024"
                  height="817"
                />
                <Disclosure.Panel>
                  <pre className="text-red-400 text-xs overflow-auto max-h-64">
                    {componentStack}
                  </pre>
                </Disclosure.Panel>
              </div>
            </>
          )}
        </Disclosure>
      </div>
    </div>
  );
};

/** Staff-only debug panel showing full error details. */
const StaffDebugPanel: React.FC<{
  error: Error;
  componentStack?: string;
  eventId?: string;
}> = ({ error, componentStack, eventId }) => (
  <div className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 mb-4 text-xs font-mono overflow-auto max-h-64">
    <p className="text-slate-500 mb-1">Staff Debug Info</p>
    {eventId && (
      <p className="text-slate-400">
        <span className="text-slate-500">Sentry:</span> {eventId}
      </p>
    )}
    <p className="text-red-400">
      <span className="text-slate-500">Error:</span> {error.name}: {error.message}
    </p>
    {error.stack && (
      <details className="mt-2">
        <summary className="text-slate-500 cursor-pointer hover:text-slate-400">
          Error stack
        </summary>
        <pre className="text-slate-400 mt-1 whitespace-pre-wrap">{error.stack}</pre>
      </details>
    )}
    {componentStack && (
      <details className="mt-2">
        <summary className="text-slate-500 cursor-pointer hover:text-slate-400">
          Component stack
        </summary>
        <pre className="text-slate-400 mt-1 whitespace-pre-wrap">{componentStack}</pre>
      </details>
    )}
  </div>
);

export default ErrorBoundary;
